import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import CourseOutlineTemplate from './CourseOutlineTemplate';
import AssessmentRubrics from './AssessmentRubrics';
import EvaluationTemplates from './EvaluationTemplates';
import EngagementChecklist from './EngagementChecklist';
import LearningObjectivesGuide from './LearningObjectivesGuide';
import { ArrowLeft, FileText, CheckSquare, Users } from 'lucide-react';

const ResourceManager = () => {
  const [currentResource, setCurrentResource] = useState<string | null>(null);

  const resources = {
    'course-outline-template': CourseOutlineTemplate,
    'learning-objectives-guide': LearningObjectivesGuide,
    'assessment-rubrics': AssessmentRubrics,
    'evaluation-templates': EvaluationTemplates,
    'engagement-checklist': EngagementChecklist,
  };

  const ResourceCard = ({ title, description, category, onClick, comingSoon = false }: {
    title: string;
    description: string;
    category: string;
    onClick: () => void;
    comingSoon?: boolean;
  }) => (
    <div 
      className={`p-6 rounded-lg border transition-all cursor-pointer ${
        comingSoon 
          ? 'bg-muted/50 border-muted-foreground/20 opacity-60' 
          : 'bg-gradient-card border-border hover:shadow-lg hover:scale-105'
      }`}
      onClick={!comingSoon ? onClick : undefined}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          {category === 'Templates & Guides' ? (
            <FileText className="w-5 h-5 text-primary" />
          ) : category === 'Quick References' ? (
            <CheckSquare className="w-5 h-5 text-primary" />
          ) : (
            <Users className="w-5 h-5 text-primary" />
          )}
          <span className="text-xs font-semibold text-primary uppercase tracking-wide">
            {category}
          </span>
        </div>
        {comingSoon && (
          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
            Coming Soon
          </span>
        )}
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );

  if (currentResource) {
    const ResourceComponent = resources[currentResource as keyof typeof resources];
    return (
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b p-4">
          <Button
            onClick={() => setCurrentResource(null)}
            variant="ghost"
            className="flex items-center gap-2 text-primary hover:text-primary/80"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Button>
        </div>
        <ResourceComponent />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Course Creation Academy Resources
        </h1>
        <p className="text-muted-foreground text-lg">
          Professional templates, guides, and references to help you create outstanding professional development courses.
        </p>
      </div>

      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Templates & Guides
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ResourceCard
              title="Course Outline Template"
              description="Comprehensive template for structuring your course content"
              category="Templates & Guides"
              onClick={() => setCurrentResource('course-outline-template')}
            />
            <ResourceCard
              title="Learning Objectives Guide"
              description="Step-by-step guide to writing effective learning objectives"
              category="Templates & Guides"
              onClick={() => setCurrentResource('learning-objectives-guide')}
            />
            <ResourceCard
              title="Assessment Rubrics"
              description="Professional rubrics for evaluating participant performance"
              category="Templates & Guides"
              onClick={() => setCurrentResource('assessment-rubrics')}
            />
            <ResourceCard
              title="Evaluation Templates"
              description="Course and instructor evaluation forms"
              category="Templates & Guides"
              onClick={() => setCurrentResource('evaluation-templates')}
            />
            <ResourceCard
              title="Engagement Checklist"
              description="Strategies to keep participants actively engaged"
              category="Templates & Guides"
              onClick={() => setCurrentResource('engagement-checklist')}
            />
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <CheckSquare className="w-6 h-6 text-primary" />
            Quick References
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ResourceCard
              title="Bloom's Taxonomy Reference"
              description="Quick reference for creating learning objectives at different cognitive levels"
              category="Quick References"
              onClick={() => console.log('Coming soon: Blooms Taxonomy Reference')}
              comingSoon
            />
            <ResourceCard
              title="Instructional Design Models"
              description="Overview of popular instructional design frameworks"
              category="Quick References"
              onClick={() => console.log('Coming soon: Instructional Design Models')}
              comingSoon
            />
            <ResourceCard
              title="Adult Learning Principles"
              description="Key principles for designing effective adult education"
              category="Quick References"
              onClick={() => console.log('Coming soon: Adult Learning Principles')}
              comingSoon
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceManager;